import { z } from 'zod';
// ── Reference lists (M5-09) ───────────────────────────────────────────────────
export const REFERENCE_LISTS = [
    'channel',
    'outcome',
    'lost_reason',
    'disqualify_reason',
    'persona',
    'data_source',
];
/** Semantic kinds the system reacts to; Admins may add items but each outcome/channel maps to a kind. */
export const CHANNEL_KINDS = [
    'linkedin_connect',
    'linkedin_message',
    'email',
    'call',
    'whatsapp',
    'linkedin_post',
    'other',
];
export const OUTCOME_KINDS = [
    'no_response',
    'positive',
    'neutral',
    'not_now',
    'declined',
    'bounced',
    'wrong_person',
];
const itemKey = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z][a-z0-9_]{1,39}$/, 'Lowercase letters, numbers and underscores');
export const createReferenceItemSchema = z.object({
    list: z.enum(REFERENCE_LISTS),
    key: itemKey,
    label: z.string().trim().min(1).max(80),
    kind: z.string().nullish(),
    sortOrder: z.number().int().min(0).max(999).optional(),
});
export const updateReferenceItemSchema = z
    .object({
    label: z.string().trim().min(1).max(80),
    sortOrder: z.number().int().min(0).max(999),
    active: z.boolean(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'Nothing to update');
const withKind = (items) => items.map(([key, label]) => ({ key, label, kind: key }));
/** Starting lists from the requirements (§4 M4-02/03, §7.1 exits). Seeded per tenant on first use. */
export const DEFAULT_REFERENCE_ITEMS = {
    channel: withKind([
        ['linkedin_connect', 'LinkedIn connect'],
        ['linkedin_message', 'LinkedIn message'],
        ['email', 'Email'],
        ['call', 'Call'],
        ['whatsapp', 'WhatsApp'],
        ['linkedin_post', 'LinkedIn post or group'],
        ['other', 'Other'],
    ]),
    outcome: withKind([
        ['no_response', 'No response'],
        ['positive', 'Positive'],
        ['neutral', 'Neutral'],
        ['not_now', 'Not now'],
        ['declined', 'Declined'],
        ['bounced', 'Bounced'],
        ['wrong_person', 'Wrong person'],
    ]),
    lost_reason: [
        { key: 'price', label: 'Price' },
        { key: 'incumbent_vendor', label: 'Incumbent vendor' },
        { key: 'no_budget', label: 'No budget' },
        { key: 'went_silent', label: 'Went silent' },
        { key: 'hired_internally', label: 'Hired internally' },
        { key: 'requirement_cancelled', label: 'Requirement cancelled' },
        { key: 'other', label: 'Other' },
    ],
    disqualify_reason: [
        { key: 'wrong_company_type', label: 'Wrong company type' },
        { key: 'below_size', label: 'Below size' },
        { key: 'staffing_firm', label: 'Staffing firm' },
        { key: 'outside_geography', label: 'Outside geography' },
        { key: 'duplicate', label: 'Duplicate' },
    ],
    persona: [
        { key: 'founder', label: 'Founder / CEO' },
        { key: 'cto', label: 'CTO / VP Engineering' },
        { key: 'engineering_manager', label: 'Engineering manager' },
        { key: 'ta_head', label: 'HR / Talent acquisition head' },
        { key: 'delivery_head', label: 'Delivery head' },
        { key: 'other', label: 'Other' },
    ],
    data_source: [
        { key: 'sales_navigator', label: 'LinkedIn Sales Navigator' },
        { key: 'warm_network', label: 'Warm network' },
        { key: 'referral', label: 'Referral' },
        { key: 'company_website', label: 'Company website' },
        { key: 'event', label: 'Event' },
        { key: 'legacy_import', label: 'Imported from legacy Excel' },
        { key: 'ai_lookalike', label: 'AI lookalike' },
        { key: 'other', label: 'Other' },
    ],
};
export const serviceInputSchema = z.object({
    name: z.string().trim().min(2).max(120),
    description: z.string().trim().max(2000).nullish(),
    buyerSituations: z.string().trim().max(2000).nullish(),
    fitCriteria: z.string().trim().max(2000).nullish(),
    active: z.boolean().optional(),
});
export const DEFAULT_SERVICES = [
    {
        name: 'Contract & short-tenure staffing',
        description: 'Pre-vetted engineers on contract for project peaks and short engagements.',
    },
    {
        name: 'TA-gap support',
        description: 'Recruiting capacity when the internal talent-acquisition team cannot keep up.',
    },
    {
        name: 'Onboarding assurance',
        description: 'Placement follow-through so new hires join, settle and stay.',
    },
];
// ── ICP cards (M5-01/02/08) ───────────────────────────────────────────────────
export const ICP_STATUSES = ['assumed', 'validated', 'paused'];
export const HEADCOUNT_BANDS = ['1-50', '51-200', '201-500', '501-1000', '1001-5000', '5000+'];
const stringList = z.array(z.string().trim().min(1).max(80)).max(30).default([]);
export const icpInputSchema = z.object({
    name: z.string().trim().min(2).max(120),
    /** ICP0 (warm network) is a relationship ICP: described, not filtered (M5-02) */
    kind: z.enum(['filtered', 'relationship']).default('filtered'),
    status: z.enum(ICP_STATUSES).default('assumed'),
    accountFilters: z
        .object({
        industries: stringList,
        sizeBands: z.array(z.enum(HEADCOUNT_BANDS)).default([]),
        locations: stringList,
        signals: z.string().trim().max(1000).default(''),
    })
        .default({ industries: [], sizeBands: [], locations: [], signals: '' }),
    leadFilters: z
        .object({ titles: stringList, seniority: stringList, functions: stringList })
        .default({ titles: [], seniority: [], functions: [] }),
    trigger: z.string().trim().max(1000).nullish(),
    assumedPain: z.string().trim().max(1000).nullish(),
    serviceId: z.uuid().nullish(),
    persona: z.string().trim().max(200).nullish(),
    callToAction: z.string().trim().max(500).nullish(),
    minSample: z.number().int().min(1).max(10_000).default(40),
    listQualityPct: z.number().min(0).max(100).nullish(),
    relationshipDescription: z.string().trim().max(2000).nullish(),
    salesNavigatorRecipe: z.string().trim().max(4000).nullish(),
});
export const updateIcpSchema = icpInputSchema
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'Nothing to update');
// ── Pitch library (M5-04) ────────────────────────────────────────────────────
export const PITCH_STATUSES = ['draft', 'approved', 'retired'];
const pitchChannelFields = {
    connectionNote: z.string().trim().max(300),
    message: z.string().trim().max(3000),
    emailSubject: z.string().trim().max(150),
    emailBody: z.string().trim().max(5000),
    callOpener: z.string().trim().max(2000),
    post: z.string().trim().max(3000),
};
const pitchModuleFields = {
    hook: z.string().trim().max(1000),
    proof: z.string().trim().max(1000),
    offer: z.string().trim().max(1000),
    ask: z.string().trim().max(1000),
};
const withEmptyDefaults = (fields) => z.object(Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, v.default('')])));
export const pitchContentSchema = z.object({
    channels: withEmptyDefaults(pitchChannelFields).default({}),
    modules: withEmptyDefaults(pitchModuleFields).default({}),
});
export const createPitchSchema = pitchContentSchema.extend({
    icpId: z.uuid(),
    name: z.string().trim().min(2).max(120),
});
/** Only the fields sent are changed; everything else keeps the current draft's text. */
export const updatePitchDraftSchema = z
    .object({
    name: z.string().trim().min(2).max(120),
    channels: z.object(pitchChannelFields).partial(),
    modules: z.object(pitchModuleFields).partial(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'Nothing to update');
// ── Playbook settings (M5-06) ────────────────────────────────────────────────
const days = z.number().int().min(1).max(365);
const ratio = z.number().min(0).max(1);
export const playbookSettingsSchema = z.object({
    /** §7.1 age limits (starting values) — drive card colours: green ≤ limit, amber ≤ 2×, red beyond */
    stageAgeLimits: z.object({
        identified: days,
        contacted: days,
        engaged: days,
        discovery: days,
        requirement: days,
        commercials: days,
        /** days from Won to first placement */
        won: days,
    }),
    /** weights for the weighted potential-revenue toggle (M7-06) */
    stageWeights: z.object({
        identified: ratio,
        contacted: ratio,
        engaged: ratio,
        discovery: ratio,
        requirement: ratio,
        commercials: ratio,
        won: ratio,
    }),
    /** M1-02 day-plan priority order */
    priorityOrder: z
        .array(z.enum(['closures', 'meetings', 'follow_ups', 'outreach', 'research']))
        .length(5)
        .refine((v) => new Set(v).size === 5, 'Each priority appears once'),
    quotas: z.object({
        outreachPerDay: z.number().int().min(0).max(500),
        followUpsPerDay: z.number().int().min(0).max(500),
        researchPerDay: z.number().int().min(0).max(500),
        /** per-ICP daily outreach overrides */
        perIcp: z.array(z.object({ icpId: z.uuid(), outreachPerDay: z.number().int().min(0).max(500) })).max(50),
    }),
    cadence: z.object({
        steps: z
            .array(z.object({
            dayOffset: z.number().int().min(0).max(90),
            channel: z.string().min(1),
            action: z.string().trim().min(1).max(120),
        }))
            .min(1)
            .max(20),
        /** M8-02: at most N touches per channel in any three-day period */
        maxTouchesPerChannelPer3Days: z.number().int().min(1).max(5),
    }),
    rag: z.object({
        lead: z.object({ nextActionWithinDays: days, amberIdleDays: days, redIdleDays: days }),
        activity: z.object({ green: ratio, amber: ratio }),
        experiment: z.object({ minSample: z.number().int().min(1).max(10_000), listQualityMin: ratio }),
        pipelineCoverage: z.object({ green: z.number().min(0).max(20), amber: z.number().min(0).max(20) }),
    }),
    schedules: z.object({
        analyzer: z.string().min(9),
        dayEndReport: z.string().min(9),
        weekendReport: z.string().min(9),
    }),
    consistency: z.object({
        tolerancePct: z.number().int().min(0).max(100),
        activityGapMinutes: z.number().int().min(5).max(240),
        learningPeriodWorkingDays: z.number().int().min(0).max(60),
    }),
});
/** Starting values from the requirements (§7.1, §7.3, §10). All configurable by the Admin. */
export const DEFAULT_PLAYBOOK_SETTINGS = {
    stageAgeLimits: {
        identified: 3,
        contacted: 21,
        engaged: 7,
        discovery: 7,
        requirement: 10,
        commercials: 14,
        won: 30,
    },
    stageWeights: {
        identified: 0.05,
        contacted: 0.1,
        engaged: 0.2,
        discovery: 0.35,
        requirement: 0.5,
        commercials: 0.75,
        won: 1,
    },
    priorityOrder: ['closures', 'meetings', 'follow_ups', 'outreach', 'research'],
    quotas: { outreachPerDay: 15, followUpsPerDay: 20, researchPerDay: 5, perIcp: [] },
    cadence: {
        steps: [
            { dayOffset: 0, channel: 'linkedin_connect', action: 'Send connection request with note' },
            { dayOffset: 3, channel: 'linkedin_message', action: 'Follow-up message' },
            { dayOffset: 7, channel: 'email', action: 'Intro email' },
            { dayOffset: 14, channel: 'call', action: 'Call' },
            { dayOffset: 21, channel: 'email', action: 'Break-up email' },
        ],
        maxTouchesPerChannelPer3Days: 1,
    },
    rag: {
        lead: { nextActionWithinDays: 3, amberIdleDays: 7, redIdleDays: 14 },
        activity: { green: 0.9, amber: 0.6 },
        experiment: { minSample: 40, listQualityMin: 0.8 },
        pipelineCoverage: { green: 3, amber: 1.5 },
    },
    schedules: { analyzer: '30 7 * * *', dayEndReport: '0 21 * * *', weekendReport: '0 21 * * 6' },
    consistency: { tolerancePct: 25, activityGapMinutes: 30, learningPeriodWorkingDays: 10 },
};
export const savePlaybookDraftSchema = z.object({
    settings: playbookSettingsSchema,
    notes: z.string().trim().max(1000).nullish(),
});
// ── Library (M5-07) ──────────────────────────────────────────────────────────
export const LIBRARY_CATEGORIES = ['cheat_sheet', 'checklist', 'dos_donts', 'guide'];
export const libraryPageInputSchema = z.object({
    category: z.enum(LIBRARY_CATEGORIES),
    title: z.string().trim().min(2).max(160),
    /** Markdown */
    body: z.string().max(50_000).default(''),
    tags: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
    /** contextual links: e.g. the Discovery checklist opens when a card enters Discovery */
    stages: z.array(z.string()).max(10).default([]),
    reviewDate: z.iso.date().nullish(),
    status: z.enum(['draft', 'published']).default('published'),
});
export const updateLibraryPageSchema = libraryPageInputSchema
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'Nothing to update');
//# sourceMappingURL=playbook.js.map